import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SectionHeading } from '../components/shared/SectionHeading';
import { ServiceCard } from '../components/shared/ServiceCard';
import { services } from '../data/mock';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Services() {
  const { t } = useLanguage();
  
  const processSteps = [
    { step: '01', title: t.servicesPage.process.steps[0].title, desc: t.servicesPage.process.steps[0].desc },
    { step: '02', title: t.servicesPage.process.steps[1].title, desc: t.servicesPage.process.steps[1].desc },
    { step: '03', title: t.servicesPage.process.steps[2].title, desc: t.servicesPage.process.steps[2].desc },
    { step: '04', title: t.servicesPage.process.steps[3].title, desc: t.servicesPage.process.steps[3].desc }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/20" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm font-medium mb-8">
              {t.servicesPage.badge}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight mb-6">
              {t.servicesPage.title}
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-400">
              {t.servicesPage.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => (
              <div key={service.id} id={service.id}>
                <ServiceCard service={service} variant="detailed" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.servicesPage.process.badge}
            title={t.servicesPage.process.title}
            subtitle={t.servicesPage.process.subtitle}
            centered
          />
          
          <div className="grid md:grid-cols-4 gap-8 mt-16">
            {processSteps.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-7xl font-bold text-slate-100 dark:text-slate-800 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 right-0 w-full h-px bg-gradient-to-r from-slate-300 dark:from-slate-700 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.servicesPage.ctaTitle}
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            {t.servicesPage.ctaSubtitle}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 px-8 text-base">
              {t.servicesPage.requestQuote}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
